import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { getPosts, getMediaUrl } from "@/lib/payload";
import { SITE_URL } from "@/lib/constants";

const NewHeader = dynamic(
  () => import("@/components/NewHeader").then((mod) => mod.default),
  { ssr: true }
);

const Footer = dynamic(
  () => import("@/components/homepages/Footer").then((mod) => mod.default),
  { ssr: true }
);

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function BlogCard({ post }) {
  const imageUrl = getMediaUrl(post.featuredImage);
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group bg-base-white rounded-[20px] overflow-hidden border border-black/[0.04]
                 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all duration-300
                 hover:-translate-y-1 flex flex-col"
    >
      {imageUrl && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <Image
            src={imageUrl}
            alt={post.featuredImage?.alt || post.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        </div>
      )}
      <div className="p-7 flex flex-col flex-1">
        {post.categories?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {post.categories.map((cat) => (
              <span
                key={cat.id || cat}
                className="text-[12px] font-semibold text-blue-primary-500 bg-blue-primary-50
                           px-3 py-1 rounded-full uppercase tracking-[0.5px]"
              >
                {cat.name || cat}
              </span>
            ))}
          </div>
        )}
        <h3 className="font-inter text-[18px] font-bold text-gray-cool-900 leading-[1.4] mb-3 flex-1">
          {post.title}
        </h3>
        <p className="font-inter text-[14px] text-gray-cool-500 leading-[1.7] mb-4 line-clamp-2">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between mt-auto">
          <span className="font-inter text-[13px] text-gray-cool-400">
            {post.publishedAt ? formatDate(post.publishedAt) : ""}
          </span>
          <span className="text-blue-primary-500 font-semibold text-[14px] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
            Read More
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogIndex({ posts }) {
  return (
    <>
      <Head>
        <title>Blog | Ledgerowl - AI Bookkeeping Australia</title>
        <meta
          name="description"
          content="Insights, tips, and guides on AI bookkeeping, accounting automation, and financial management for Australian businesses."
        />
        <meta
          property="og:title"
          content="Blog | Ledgerowl - AI Bookkeeping Australia"
        />
        <meta
          property="og:description"
          content="Insights, tips, and guides on AI bookkeeping, accounting automation, and financial management for Australian businesses."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content="Blog | Ledgerowl - AI Bookkeeping Australia"
        />
        <meta
          name="twitter:description"
          content="Insights, tips, and guides on AI bookkeeping, accounting automation, and financial management for Australian businesses."
        />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <link rel="icon" href="/favicon.png" />
      </Head>

      <NewHeader />

      <section className="pt-[140px] pb-[120px] px-6 lg:px-10 bg-cream min-h-screen">
        <div className="max-w-[1340px] mx-auto">
          <h1 className="font-inter text-[36px] sm:text-[42px] lg:text-[54px] font-extrabold tracking-[-1.5px] text-gray-cool-900 mb-4">
            Blog
          </h1>
          <p className="font-inter text-[17px] text-gray-cool-500 leading-[1.8] mb-[60px] max-w-2xl">
            Insights on AI bookkeeping, accounting best practices, and business
            finance for Australian SMEs.
          </p>

          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="font-inter text-[18px] text-gray-cool-400">
                No blog posts yet. Check back soon!
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}

export async function getStaticProps() {
  try {
    const data = await getPosts({ page: 1, limit: 100 });
    return {
      props: {
        posts: data.docs || [],
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error("Error fetching blog posts:", error);
    return {
      props: {
        posts: [],
      },
      revalidate: 60,
    };
  }
}
